import { ReactElement } from "react";
import { Icon, FontIconName } from "../Icon/Icon";
import classNames from "classnames";
import styles from "./PersonAvatarStatus.module.less";
import { localize } from "client_legacy/ndaw/culture";

export const enum PersonAvatarStatusKind {
	Accept = "accept",
	Decline = "decline",
	Tenative = "tenative",
	Unknown = "unknown",
	Available = "available",
	Busy = "busy",
	DoNotDisturb = "do-not-disturb",
	Away = "away",
	BeRightBack = "be-right-back",
	Offline = "offline",
	OutOfOffice = "out-of-office"
}

const statusToIconMap: { [key in PersonAvatarStatusKind]: FontIconName | null } = {
	[PersonAvatarStatusKind.Accept]: "check",
	[PersonAvatarStatusKind.Decline]: "xmark",
	[PersonAvatarStatusKind.Tenative]: "question",
	[PersonAvatarStatusKind.Available]: "check",
	[PersonAvatarStatusKind.Busy]: null,
	[PersonAvatarStatusKind.DoNotDisturb]: "minus",
	[PersonAvatarStatusKind.Away]: "clock",
	[PersonAvatarStatusKind.BeRightBack]: "clock",
	[PersonAvatarStatusKind.Offline]: "xmark",
	[PersonAvatarStatusKind.OutOfOffice]: "arrow-left",
	[PersonAvatarStatusKind.Unknown]: "minus",
};

const statusToTooltipKeyMap: { [key in PersonAvatarStatusKind]: LocalizationKey; } = {
	[PersonAvatarStatusKind.Accept]: "_Yes",
	[PersonAvatarStatusKind.Decline]: "_No",
	[PersonAvatarStatusKind.Tenative]: "_Maybe",
	[PersonAvatarStatusKind.Unknown]: "_Unknown",
	[PersonAvatarStatusKind.Available]: "_Available",
	[PersonAvatarStatusKind.Busy]: "_Busy",
	[PersonAvatarStatusKind.DoNotDisturb]: "_DoNotDisturb",
	[PersonAvatarStatusKind.Away]: "_Away",
	[PersonAvatarStatusKind.BeRightBack]: "_BeRightBack",
	[PersonAvatarStatusKind.Offline]: "_Offline",
	[PersonAvatarStatusKind.OutOfOffice]: "_OutOfOffice"
};

const statusToStylesMap: { [key in PersonAvatarStatusKind]: string } = {
	[PersonAvatarStatusKind.Accept]: styles.root_accept,
	[PersonAvatarStatusKind.Unknown]: styles.root_unknown,
	[PersonAvatarStatusKind.Decline]: styles.root_decline,
	[PersonAvatarStatusKind.Tenative]: styles.root_tenative,
	[PersonAvatarStatusKind.Available]: styles.root_available,
	[PersonAvatarStatusKind.Busy]: styles.root_busy,
	[PersonAvatarStatusKind.DoNotDisturb]: styles.root_dnd,
	[PersonAvatarStatusKind.Away]: styles.root_away,
	[PersonAvatarStatusKind.BeRightBack]: styles.root_brb,
	[PersonAvatarStatusKind.Offline]: styles.root_offline,
	[PersonAvatarStatusKind.OutOfOffice]: styles.root_oof,
};

export type PersonAvatarStatusSize =
	| "x-small"
	| "small"
	| "default"
	| "large"
	| "x-large"
	| "xx-large"
	| "xxx-large";

const sizeToStyle: {[key in PersonAvatarStatusSize]: string | null} = {
	"x-small": styles.root_xSmall,
	"small": styles.root_small,
	"default": null,
	"large": styles.root_large,
	"x-large": styles.root_xLarge,
	"xx-large": styles.root_xxLarge,
	"xxx-large": styles.root_xxxLarge
};

export type PersonAvatarStatusProps = {
	kind: PersonAvatarStatusKind;
	size?: PersonAvatarStatusSize;
};

export const PersonAvatarStatus = (props: PersonAvatarStatusProps): ReactElement => {
	const {
		kind,
		size = "default"
	} = props;

	const icon: FontIconName | null = statusToIconMap[kind];

	return (
		<div
			className={classNames(
				styles.root,
				statusToStylesMap[kind],
				sizeToStyle[size]
			)}
			title={localize(statusToTooltipKeyMap[kind])}
		>
			{icon && (
				<Icon value={icon} />
			)}
		</div>
	);
};
PersonAvatarStatus.displayName = "PersonAvatarStatus";
